import { PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import {
  createClientTabAdmin,
  deleteClientTabAdmin,
  listClientTabsAdmin,
  reorderClientTabsAdmin,
  updateClientTabAdmin,
} from 'api/clientTabsAdmin';
import {
  AVAILABLE_ICONS,
  getTabDefaults,
  listRegistryKeys,
  resolveIcon,
} from 'providers/ClientTabs/clientTabsRegistry';
import SortableTabsTable from 'providers/ClientTabs/SortableTabsTable';
import { useEffect, useMemo, useState } from 'react';

const USER_TYPE = 'ejecutivo';

// Drawer que usa el supertenant para configurar qué tabs ve el ejecutivo
// de un tenant específico. Los tabs admin se configuran aparte desde
// Administradores.jsx (Fase 5) y son un subset de estos (cascada enforced
// en el backend).
export default function ExecutiveTabsConfig({ tenant, open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const tenantId = tenant?.id;

  const refresh = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const data = await listClientTabsAdmin(tenantId, USER_TYPE);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error(err?.response?.data?.mensaje || 'No se pudieron cargar los tabs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && tenantId) refresh();
    if (!open) setRows([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tenantId]);

  const usedKeys = useMemo(() => new Set(rows.map((r) => r.key)), [rows]);
  const availableKeysToAdd = useMemo(
    () => listRegistryKeys().filter((k) => !usedKeys.has(k)),
    [usedKeys]
  );

  const commitField = async (row, field, value) => {
    if (row[field] === value) return;
    setSavingId(row.id);
    try {
      const updated = await updateClientTabAdmin(row.id, { [field]: value });
      setRows((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
    } catch (err) {
      message.error(err?.response?.data?.mensaje || 'No se pudo actualizar');
      await refresh();
    } finally {
      setSavingId(null);
    }
  };

  const onDelete = async (row) => {
    try {
      await deleteClientTabAdmin(row.id);
      message.success(
        `Tab "${row.key}" eliminado. Si estaba propagado a admin, también se eliminó ahí.`
      );
      await refresh();
    } catch (err) {
      message.error(err?.response?.data?.mensaje || 'No se pudo eliminar');
    }
  };

  const onAdd = async (values) => {
    try {
      await createClientTabAdmin({
        tenantId,
        userType: USER_TYPE,
        key: values.key,
        label: values.label,
        icon: values.icon || null,
        order: values.order ?? rows.length + 1,
      });
      message.success('Tab agregado');
      setAddOpen(false);
      await refresh();
    } catch (err) {
      message.error(err?.response?.data?.mensaje || 'No se pudo crear el tab');
    }
  };

  const onReorder = async (reorderedRows) => {
    // Optimistic: aplica el orden localmente y sincroniza en background.
    const withOrder = reorderedRows.map((row, index) => ({ ...row, order: index + 1 }));
    setRows(withOrder);
    try {
      await reorderClientTabsAdmin(
        tenantId,
        USER_TYPE,
        withOrder.map(({ id, order }) => ({ id, order }))
      );
    } catch (err) {
      message.error(err?.response?.data?.mensaje || 'No se pudo reordenar');
      await refresh();
    }
  };

  const orderedRows = useMemo(
    () => [...rows].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [rows]
  );

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width={820}
        title={
          tenant ? `Tabs del ejecutivo — ${tenant.organization}` : 'Configurar tabs de ejecutivo'
        }
        destroyOnHidden
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAddOpen(true)}
            disabled={loading || availableKeysToAdd.length === 0}
          >
            Agregar tab
          </Button>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Cascada hacia administrador"
          description="Los tabs que elimines acá también desaparecen del administrador del tenant si los tenía activos. Arrastra la manija para reordenar. El tenant solo puede habilitar a su admin tabs que vean en esta lista."
        />
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <SortableTabsTable
            rows={orderedRows}
            savingId={savingId}
            onEditField={commitField}
            onDelete={onDelete}
            onReorder={onReorder}
          />
        )}
        <Typography.Paragraph type="secondary" style={{ marginTop: 16, fontSize: 12 }}>
          Keys disponibles en el registry frontend: <code>{listRegistryKeys().join(', ')}</code>
        </Typography.Paragraph>
      </Drawer>

      <AddTabModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        availableKeys={availableKeysToAdd}
        onSubmit={onAdd}
      />
    </>
  );
}

function AddTabModal({ open, onClose, availableKeys, onSubmit }) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open) form.resetFields();
  }, [open, form]);

  const handleKeyChange = (key) => {
    const defaults = getTabDefaults(key);
    if (defaults) {
      form.setFieldsValue({
        label: defaults.label,
        icon: defaults.icon,
      });
    }
  };

  return (
    <Modal
      open={open}
      title="Agregar tab al ejecutivo"
      okText="Agregar"
      cancelText="Cancelar"
      onCancel={onClose}
      onOk={() => form.submit()}
      destroyOnHidden
    >
      <Form layout="vertical" form={form} onFinish={onSubmit}>
        <Form.Item
          label="Key"
          name="key"
          rules={[{ required: true, message: 'Seleccioná un key' }]}
        >
          <Select
            placeholder="Elegí un key del registry"
            onChange={handleKeyChange}
            options={availableKeys.map((k) => ({ value: k, label: k }))}
            showSearch
          />
        </Form.Item>
        <Form.Item label="Label" name="label" rules={[{ required: true, message: 'Requerido' }]}>
          <Input placeholder="Texto visible en la UI" />
        </Form.Item>
        <Form.Item label="Ícono" name="icon">
          <Select
            allowClear
            placeholder="Ícono opcional"
            options={AVAILABLE_ICONS.map((name) => ({
              value: name,
              label: (
                <Space>
                  {resolveIcon(name)}
                  <span>{name}</span>
                </Space>
              ),
            }))}
          />
        </Form.Item>
        <Form.Item label="Orden" name="order">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
